/**
 * BatchCreationProgress: the live Create Batch ticker (BLU-1330 PR 2b).
 *
 * Rides in the Order Queue's FloatingSelectionBar, in the slot the
 * include-redos card uses, so pressing Create Batch turns that card into the
 * ticker without moving anything. After a reload mid-batch it floats alone.
 *
 * Production desk-side surface (PRODUCTION_DESIGN.md §4, §5.3, §13):
 *   - amber = in progress (semantic, not brand purple);
 *   - the count is the headline, `text-lg` + `tabular-nums`, so the digits
 *     tick in place instead of jittering the line;
 *   - the bar glides to each 2 s poll (transform only, ease-out) instead of
 *     jumping; until the total is known it slides with the app loading bar;
 *   - reduced motion: the spinner stops and the bar steps, but the count and
 *     the bar still change, so the operator still sees it moving;
 *   - ONE polite `role="status"` message per state (creating / checking), never
 *     one per tick. The ticking text is aria-hidden; the progressbar carries
 *     the same words as its value for anyone who navigates to it.
 *
 * `busy`: a colleague's run holds the location (one Create Batch per org per
 * location; another org's run at a shared location never shows). One calm
 * line in the same card, no count and no bar: this operator has nothing in
 * flight, they are only waiting their turn.
 */
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import type { BatchCreationView } from '../hooks/useBatchCreationStatus';

interface BatchCreationProgressProps {
  view: BatchCreationView;
  className?: string;
}

const CARD_CLASS =
  'w-[min(22rem,calc(100vw-2rem))] rounded-2xl border bg-card px-4 py-3 shadow-lg motion-safe:animate-fade-in';

export function BatchCreationProgress({ view, className }: BatchCreationProgressProps) {
  const { t } = useTranslation('production');
  const { entry, kind } = view;
  const checking = kind === 'checking';

  if (kind === 'busy') {
    return (
      <div data-testid="batch-creation-progress" className={cn(CARD_CLASS, className)}>
        <p
          role="status"
          aria-live="polite"
          data-testid="batch-creation-busy"
          className="flex items-start gap-2 text-sm font-medium text-muted-foreground"
        >
          <Loader2
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-amber-600 motion-reduce:animate-none"
          />
          <span className="min-w-0 break-words">{t('batch.progress.locationBusy')}</span>
        </p>
      </div>
    );
  }

  // null until the backend has counted the batch's garments.
  const total = entry?.unitsTotal != null && entry.unitsTotal > 0 ? entry.unitsTotal : null;
  const saved = total === null ? 0 : Math.min(entry?.unitsSaved ?? 0, total);
  const allSaved =
    total !== null && (saved >= total || entry?.phase === 'finishing' || entry?.phase === 'done');
  const percent = total === null ? null : allSaved ? 100 : (saved / total) * 100;

  // A full bar that sits still reads as stuck, so the title says what is still
  // happening. The count line keeps its shape, so nothing reflows at the end.
  let title: string;
  if (checking) title = t('batch.progress.checking');
  else if (allSaved) title = t('batch.progress.finishing');
  else title = t('batch.progress.creating');

  let headline: string;
  if (!entry && checking) headline = t('batch.progress.lookingForBatch');
  else if (total === null) headline = t('batch.progress.counting');
  else headline = t('batch.progress.saved', { saved, total });

  return (
    <div data-testid="batch-creation-progress" className={cn(CARD_CLASS, className)}>
      <span role="status" aria-live="polite" className="sr-only">
        {checking ? t('batch.progress.srChecking') : t('batch.progress.srCreating')}
      </span>
      {/* The spinner rides the title so the count gets the card's full width:
          "10,000 of 10,000 garments saved" fits one line in en and zh. */}
      <div aria-hidden="true" className="min-w-0 break-words">
        <p className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-amber-600 motion-reduce:animate-none" />
          {title}
        </p>
        <p data-testid="batch-creation-progress-count" className="mt-1 text-lg font-semibold tabular-nums leading-snug">
          {headline}
        </p>
      </div>
      <Progress
        className="mt-3 h-2 bg-amber-100"
        indicatorClassName="bg-amber-600 duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]"
        indeterminate={percent === null}
        value={percent}
        aria-label={t('batch.progress.barLabel')}
        getValueLabel={() => headline}
      />
    </div>
  );
}
